/**
 * Fiche de vie d'un GRV : historique d'événements en lecture seule.
 *
 * Présentation en lignes structurées plutôt qu'en frise verticale : une ligne par événement, du plus
 * récent au plus ancien par défaut, cohérente avec les autres tableaux de l'application (Positions,
 * Mouvements, Maintenance, Documents).
 *
 * Pur historique de consultation (`docs/DECISIONS.md` D89) : créer un événement se fait depuis le
 * nouvel onglet « Action » (opération de maintenance, changement de tracker, changement de statut),
 * jamais depuis cet écran.
 *
 * La date affichée est `date_evenement` (la date métier), pas `cree_le` (la date de saisie) : un
 * événement montre d'abord ce que l'utilisateur cherche, la traçabilité de création/modification
 * reste visible mais secondaire, en petit sous la date (`docs/DECISIONS.md` D80).
 */
import { useMemo } from "react";

import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDate, formaterDateSeule } from "../../shared/composants/formatage";
import type { EvenementFicheDeVie } from "../../shared/api/types";
import { useFicheDeVie } from "./requetes";

type Colonne = "type_evenement" | "date_evenement" | "auteur_email";

function valeurDe(evenement: EvenementFicheDeVie, colonne: Colonne): string | null | undefined {
  return evenement[colonne];
}

export function OngletFicheDeVie({ numero }: { numero: string }) {
  const requete = useFicheDeVie(numero);
  const { tri, basculerTri } = useTriTableau<Colonne>({
    colonne: "date_evenement",
    descendant: true,
  });

  const evenements = useMemo(() => requete.data?.data ?? [], [requete.data]);
  const triees = useTrie(evenements, tri, valeurDe);

  return (
    <div>
      {requete.isPending && <Chargement libelle="Chargement de la fiche de vie…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && triees.length === 0 && <Vide libelle="Aucun événement enregistré." />}
      {requete.data && triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable
                colonne="type_evenement"
                libelle="Type d'événement"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="date_evenement"
                libelle="Date"
                tri={tri}
                basculerTri={basculerTri}
              />
              <th scope="col">Description</th>
              <EnTeteTriable
                colonne="auteur_email"
                libelle="Auteur"
                tri={tri}
                basculerTri={basculerTri}
              />
            </tr>
          </thead>
          <tbody>
            {triees.map((evenement, index) => (
              <tr key={evenement.event_id ?? `${evenement.cree_le}-${index}`}>
                <td>{evenement.type_evenement}</td>
                <td>
                  {formaterDateSeule(evenement.date_evenement)}
                  <div className="fiche-de-vie__tracabilite">
                    Créé le {formaterDate(evenement.cree_le)}
                    {evenement.cree_par ? ` par ${evenement.cree_par}` : ""}
                    {evenement.modifie_le && (
                      <>
                        {" · Modifié le "}
                        {formaterDate(evenement.modifie_le)}
                        {evenement.modifie_par ? ` par ${evenement.modifie_par}` : ""}
                      </>
                    )}
                  </div>
                </td>
                <td>{evenement.description}</td>
                <td>{evenement.auteur_email ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
